import { BlockedRecordBlockedScope } from '@exam-center/graphql';
import {
  Box,
  CalendarTodayIcon,
  Flex,
  FormControl,
  FormDateInput,
  FormHelperText,
  FormRadio,
  FormRadioSelection,
  Grid,
  type OptionSelectionProps,
  RadioGroup,
  ReadonlyField,
  Text,
} from '@shared/components';
import { formatDate } from '@shared/utils';

import {
  BlockedCoursesSelectionField,
  type BlockedCoursesSelectionFieldProps,
} from '../blocked-courses-selection-field';
import {
  BlockedEndDateSettingSection,
  type BlockedEndDateSettingSectionProps,
} from '../blocked-end-date-setting-section';
import { BlockedReasonField } from '../blocked-reason-field';

const BLOCKED_SCOPE_OPTIONS: OptionSelectionProps[] = [
  {
    label: 'ระงับสิทธิ์ทุกหลักสูตร',
    value: BlockedRecordBlockedScope.AllScope,
  },
  { label: 'ระงับสิทธิ์เฉพาะหลักสูตรที่เลือก', value: BlockedRecordBlockedScope.PartialScope },
];

interface BlocklistEndDateReadonlyFieldsProps {
  enforcementDate: Date | null;
  blockedUntil: Date | null;
}

function BlocklistEndDateReadonlyFields(props: Readonly<BlocklistEndDateReadonlyFieldsProps>) {
  const { enforcementDate, blockedUntil } = props;

  if (enforcementDate === null && blockedUntil === null) {
    return <Text>ไม่มีกำหนด</Text>;
  }

  return (
    <Flex
      sx={(theme) => ({
        gap: theme.spacing(4),
        alignItems: 'flex-end',
      })}
    >
      <ReadonlyField
        label='วันที่สิ้นสุดปฏิบัติหน้าที่'
        content={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(1),
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text>{formatDate(enforcementDate)}</Text>
            <CalendarTodayIcon
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            />
          </Flex>
        }
        sx={{ width: '100%', maxWidth: '212px' }}
      />
      <Text
        variant='body2'
        sx={(theme) => ({
          pb: theme.spacing(3),
        })}
      >
        (วันที่สิ้นสุดระงับสิทธิ์: {formatDate(blockedUntil)})
      </Text>
    </Flex>
  );
}

interface BasicFields {
  blockedCourse: {
    type: {
      name: string;
    };
    courses: BlockedCoursesSelectionFieldProps;
  };
  reason: {
    name: string;
  };
}

export type BlocklistInformationFormFieldsProps =
  | {
      mode: 'create';
      fields: BasicFields & {
        blockedAt: {
          name: string;
          required?: boolean;
        };
        enforcementEnd: BlockedEndDateSettingSectionProps & {
          assign: {
            name: string;
            error?: string;
          };
        };
      };
    }
  | {
      mode: 'edit';
      fields: BasicFields & {
        blockedAt: Date;
        blockedUntil: Date | null;
        enforcementDate: Date | null;
      };
    };

export default function BlocklistInformationFormFields(
  props: Readonly<BlocklistInformationFormFieldsProps>,
) {
  const { mode, fields } = props;

  const isCreateMode = mode === 'create';

  return (
    <Grid
      sx={{
        gridTemplateColumns: '240px 1fr',
        gap: (theme) => theme.spacing(6),
      }}
    >
      <Text variant='body1Semibold'>วันที่เริ่มปฏิบัติหน้าที่</Text>
      <Box sx={{ maxWidth: '212px' }}>
        {isCreateMode ? (
          <FormDateInput
            {...fields.blockedAt}
            label='วันที่เริ่มปฏิบัติหน้าที่'
            placeholder='DD-MM-YYYY'
            inputFormat='DD-MM-YYYY'
          />
        ) : (
          <ReadonlyField
            label='วันที่เริ่มปฏิบัติหน้าที่'
            content={
              <Flex
                sx={{
                  gap: (theme) => theme.spacing(1),
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Text>{formatDate(fields.blockedAt)}</Text>
                <CalendarTodayIcon
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                />
              </Flex>
            }
          />
        )}
      </Box>
      <Text variant='body1Semibold'>วันที่สิ้นสุดปฏิบัติหน้าที่</Text>
      {isCreateMode ? (
        <FormControl error={!!fields.enforcementEnd.assign.error}>
          <RadioGroup
            name={fields.enforcementEnd.assign.name}
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
              <FormRadio
                {...fields.enforcementEnd.assign}
                label='ไม่มีกำหนด'
                value='false'
                radioProps={{
                  sx: (theme) => ({
                    py: theme.spacing(0),
                  }),
                }}
              />
              <Text
                variant='caption'
                sx={(theme) => ({ ml: theme.spacing(8), color: theme.palette.text.secondary })}
              >
                ยังไม่มีกำหนดวันที่สิ้นสุดปฏิบัติหน้าที่
              </Text>
            </Flex>
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
              <FormRadio
                {...fields.enforcementEnd.assign}
                label='กำหนดวันที่สิ้นสุดปฏิบัติหน้าที่'
                value='true'
                radioProps={{
                  sx: (theme) => ({
                    py: theme.spacing(0),
                  }),
                }}
              />
              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  ml: theme.spacing(8),
                  gap: theme.spacing(2.5),
                })}
              >
                <Text variant='caption' sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  คำนวณจากระยะเวลาปฏิบัติหน้าที่หรือระบุวันที่สิ้นสุดปฏิบัติหน้าที่ด้วยตนเอง
                </Text>
                <BlockedEndDateSettingSection
                  duration={fields.enforcementEnd.duration}
                  date={fields.enforcementEnd.date}
                  blockedUntil={fields.enforcementEnd.blockedUntil}
                />
              </Flex>
            </Flex>
            {fields.enforcementEnd.assign.error ? (
              <FormHelperText>{fields.enforcementEnd.assign.error}</FormHelperText>
            ) : null}
          </RadioGroup>
        </FormControl>
      ) : (
        <BlocklistEndDateReadonlyFields
          enforcementDate={fields.enforcementDate}
          blockedUntil={fields.blockedUntil}
        />
      )}
      <Text variant='body1Semibold'>หลักสูตรที่ระงับสิทธิ์สอบ</Text>
      <Flex sx={{ flexDirection: 'column' }}>
        <FormRadioSelection
          {...fields.blockedCourse.type}
          options={BLOCKED_SCOPE_OPTIONS}
          radioProps={{
            sx: (theme) => ({
              py: theme.spacing(0),
            }),
          }}
        />
        <BlockedCoursesSelectionField {...fields.blockedCourse.courses} />
      </Flex>
      <Text variant='body1Semibold'>หมายเหตุ</Text>
      <BlockedReasonField {...fields.reason} />
    </Grid>
  );
}
